import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner, Table, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';

const ModalAgregarTimbres = ({ show, onHide, idCliente, planAsociado, onOrdenCreada }) => {
    const { token, setToken } = useToken();
    const { showErrorToast, showSuccessToast } = useToast();

    const [planesDisponibles, setPlanesDisponibles] = useState([]);
    const [busquedaPlan, setBusquedaPlan] = useState('');
    const [cargandoPlanesDisponibles, setCargandoPlanesDisponibles] = useState(false);
    const [planSeleccionado, setPlanSeleccionado] = useState(null);
    const [metodoPagoSeleccionado, setMetodoPagoSeleccionado] = useState('');
    const [formasPago, setFormasPago] = useState([]);
    const [cargandoFormasPago, setCargandoFormasPago] = useState(false);
    const [creandoOrden, setCreandoOrden] = useState(false);

    useEffect(() => {
        if (show) {
            obtenerPlanesDisponibles();
        }
    }, [show]);

    const resetState = () => {
        setBusquedaPlan('');
        setPlanSeleccionado(null);
        setMetodoPagoSeleccionado('');
        setPlanesDisponibles([]);
        setFormasPago([]);
    };

    const handleCerrar = () => {
        resetState();
        onHide();
    };

    const obtenerPlanesDisponibles = async () => {
        setCargandoPlanesDisponibles(true);
        try {
            const response = await fetch('/api/cliente/getPlanesDisponiblesFacturacionMx', {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({})
            });

            if (response.ok) {
                const data = await response.json();
                if (data?.iResponseType === 1 && data?.detalles) {
                    const detalles = JSON.parse(data.detalles);
                    const table = detalles?.table2 || detalles?.Table2 || [];
                    setPlanesDisponibles(Array.isArray(table) ? table : []);
                } else {
                    setPlanesDisponibles(Array.isArray(data) ? data : data?.table2 || []);
                }
            } else {
                if (response.status === 401) {
                    setToken(null);
                    location.href = '';
                }
                showErrorToast(`Error ${response.status}: ${response.statusText}`);
            }
        } catch (error) {
            showErrorToast('Error al obtener planes disponibles: ' + error.message);
        } finally {
            setCargandoPlanesDisponibles(false);
        }
    };

    const obtenerFormasPago = async () => {
        setCargandoFormasPago(true);
        try {
            const response = await fetch('/api/cliente/getFormasPago', {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data?.iResponseType === 1 && data?.detalles) {
                    const detalles = JSON.parse(data.detalles);
                    const table = detalles?.table2 || detalles?.Table2 || [];
                    setFormasPago(Array.isArray(table) ? table : []);
                } else {
                    setFormasPago(Array.isArray(data) ? data : data?.table2 || []);
                }
            } else {
                if (response.status === 401) {
                    setToken(null);
                    location.href = '';
                }
                showErrorToast(`Error ${response.status}: ${response.statusText}`);
            }
        } catch (error) {
            showErrorToast('Error al obtener formas de pago: ' + error.message);
        } finally {
            setCargandoFormasPago(false);
        }
    };

    const handleSeleccionarPlan = async (plan) => {
        setPlanSeleccionado(plan);
        setMetodoPagoSeleccionado('');
        await obtenerFormasPago();
    };

    const handleVolverALista = () => {
        setPlanSeleccionado(null);
        setMetodoPagoSeleccionado('');
    };

    const handleConfirmarOrden = async () => {
        if (!metodoPagoSeleccionado) {
            showErrorToast('Por favor seleccione un método de pago');
            return;
        }

        setCreandoOrden(true);
        try {
            const body = {
                idClienteAkasia: idCliente,
                vUUIDProducto: planSeleccionado.vUUIDProducto,
                idCatFormaPago: Number.parseInt(metodoPagoSeleccionado)
            };

            if (planAsociado?.idPlanFacturaMx) {
                body.idPlanFacturaMx = planAsociado.idPlanFacturaMx;
            }

            const response = await fetch('/api/orden/generarOrdenFacturacionMx', {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            });

            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1) {
                    showSuccessToast('Orden creada exitosamente');
                    if (onOrdenCreada) onOrdenCreada();
                    handleCerrar();
                } else {
                    showErrorToast(data.vMessage || 'Error al crear la orden');
                }
            } else {
                if (response.status === 401) {
                    setToken(null);
                    location.href = '';
                }
                const errorData = await response.json().catch(() => ({}));
                showErrorToast(errorData.vMessage || `Error ${response.status}: ${response.statusText}`);
            }
        } catch (error) {
            showErrorToast('Error al crear la orden: ' + error.message);
        } finally {
            setCreandoOrden(false);
        }
    };

    const planesFiltrados = planesDisponibles.filter(plan =>
        plan.vNombreProducto?.toLowerCase().includes(busquedaPlan.toLowerCase()) ||
        plan.iCantidadTimbresMx?.toString().includes(busquedaPlan)
    );

    return (
        <Modal show={show} centered onHide={handleCerrar} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{planSeleccionado ? 'Confirmar Orden' : 'Agregar Timbres'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {!planSeleccionado ? (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label>Buscar plan de facturación</Form.Label>
                            <InputGroup>
                                <InputGroup.Text>
                                    <FontAwesomeIcon icon={faSearch} />
                                </InputGroup.Text>
                                <Form.Control
                                    type="text"
                                    placeholder="Buscar por nombre o descripción..."
                                    value={busquedaPlan}
                                    onChange={(e) => setBusquedaPlan(e.target.value)}
                                />
                            </InputGroup>
                        </Form.Group>

                        {cargandoPlanesDisponibles ? (
                            <div className="text-center py-4">
                                <Spinner animation="border" />
                                <p className="mt-2">Cargando planes disponibles...</p>
                            </div>
                        ) : planesFiltrados.length === 0 ? (
                            <div className="text-center py-4 text-muted">
                                {busquedaPlan ? 'No se encontraron planes que coincidan con la búsqueda' : 'No hay planes disponibles'}
                            </div>
                        ) : (
                            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                <Table hover responsive>
                                    <thead>
                                        <tr>
                                            <th>Producto</th>
                                            <th>Cantidad Timbres</th>
                                            <th>Precio</th>
                                            <th>Moneda</th>
                                            <th style={{ width: '120px' }}>Acción</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {planesFiltrados.map((plan, index) => (
                                            <tr key={plan.vUUIDProducto || index}>
                                                <td>{plan.vNombreProducto || 'N/A'}</td>
                                                <td>{plan.iCantidadTimbresMx || 'N/A'}</td>
                                                <td>{plan.dTotal ? `$${plan.dTotal}` : 'N/A'}</td>
                                                <td>{plan.vMoneda || 'N/A'}</td>
                                                <td>
                                                    <Button
                                                        variant="success"
                                                        size="sm"
                                                        onClick={() => handleSeleccionarPlan(plan)}
                                                        className="w-100"
                                                    >
                                                        Seleccionar
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        <div className="mb-3 p-3 bg-light rounded">
                            <h6 className="mb-3">Producto seleccionado:</h6>
                            <p className="mb-2"><strong>{planSeleccionado.vNombreProducto}</strong></p>
                            <p className="mb-2">Cantidad de timbres: <strong>{planSeleccionado.iCantidadTimbresMx}</strong></p>
                            <p className="mb-0">Total: <strong>${planSeleccionado.dTotal} {planSeleccionado.vMoneda}</strong></p>
                        </div>

                        <Form.Group>
                            <Form.Label>Método de Pago</Form.Label>
                            {cargandoFormasPago ? (
                                <div className="text-center py-2">
                                    <Spinner animation="border" size="sm" />
                                    <span className="ms-2">Cargando formas de pago...</span>
                                </div>
                            ) : (
                                <Form.Select
                                    value={metodoPagoSeleccionado}
                                    onChange={(e) => setMetodoPagoSeleccionado(e.target.value)}
                                >
                                    <option value="">Seleccione un método de pago...</option>
                                    {formasPago.map((formaPago, index) => (
                                        <option
                                            key={formaPago.idCatFormaPago || index}
                                            value={formaPago.idCatFormaPago}
                                        >
                                            {formaPago.vDescripcionFormaPago}
                                        </option>
                                    ))}
                                </Form.Select>
                            )}
                        </Form.Group>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                {!planSeleccionado ? (
                    <Button variant="secondary" onClick={handleCerrar}>
                        Cancelar
                    </Button>
                ) : (
                    <>
                        <Button variant="secondary" onClick={handleVolverALista}>
                            Volver
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleConfirmarOrden}
                            disabled={creandoOrden || !metodoPagoSeleccionado}
                        >
                            {creandoOrden ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Procesando...
                                </>
                            ) : (
                                'Confirmar Orden'
                            )}
                        </Button>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
};

ModalAgregarTimbres.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    idCliente: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    planAsociado: PropTypes.object,
    onOrdenCreada: PropTypes.func
};

export default ModalAgregarTimbres;
